/**
 * Cross-Sell Rail — snippet-author runtime.
 *
 * Responsibilities:
 *   1. Add-to-cart progressive enhancement: intercept each card's native
 *      cart-add form and add via the Spectrum cart SDK in-place when present;
 *      without it the native form POST proceeds (works with JS disabled).
 *   2. Card impression + click + add-to-cart analytics.
 *
 * No scroll/arrow/progress engine — the rail is a pure-CSS horizontal scroller
 * (or a static vertical stack in the compact preset). Container-scoped: every
 * DOM read/write goes through `node` so multiple rails on the same page never
 * collide.
 */
;(() => {
  const SNIPPET_ID = 'ogeszm9s'
  const FEATURE_SLUG = 'cross_sell_rail'
  const CARD_SELECTOR = `.sai-${SNIPPET_ID}__card`
  const CARD_LINK_SELECTOR = `.sai-${SNIPPET_ID}__card-link`
  const TITLE_SELECTOR = `.sai-${SNIPPET_ID}__title`
  const ADD_FORM_SELECTOR = '[data-spectrum-add-form]'

  function noopTrack() {}

  // Wrap track so a malformed payload or downstream throw can never break the
  // rail. Telemetry is observational — surface the failure in DevTools but keep
  // navigation / add-to-cart working.
  function safeTrack(track) {
    return (name, payload) => {
      try {
        track(name, payload)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[${FEATURE_SLUG}] analytics dispatch failed`, name, err)
      }
    }
  }

  function cardTitle(card) {
    return card?.querySelector(TITLE_SELECTOR)?.textContent?.trim() || ''
  }

  function cardUrl(card) {
    return card?.querySelector(CARD_LINK_SELECTOR)?.getAttribute('href') || ''
  }

  function initRail(node, track) {
    const cards = Array.from(node.querySelectorAll(CARD_SELECTOR))
    if (cards.length === 0) return

    // Product click tracking — fires before the browser commits navigation so
    // the event lands in PostHog. Native link semantics (cmd-click, middle-
    // click) still work — we never preventDefault.
    if (track) {
      for (const card of cards) {
        const link = card.querySelector(CARD_LINK_SELECTOR)
        link?.addEventListener('click', () => {
          track(`${FEATURE_SLUG}:product_click`, {
            card_index: Number(card.dataset.spectrumCardIndex || '0'),
            title: cardTitle(card),
            url: cardUrl(card),
          })
        })
      }
    }

    // Add-to-cart — progressive enhancement over the native cart-add form. When
    // the Spectrum cart SDK is present, add the variant in-place and keep the
    // shopper on the page; otherwise the native form POST proceeds.
    const addForms = node.querySelectorAll(ADD_FORM_SELECTOR)
    for (const form of addForms) {
      form.addEventListener('submit', (ev) => {
        const variantId = form.querySelector('input[name="id"]')?.value || ''
        const card = form.closest(CARD_SELECTOR)
        const cardIdx = Number(card?.dataset.spectrumCardIndex || '0')
        if (track) {
          track(`${FEATURE_SLUG}:add_to_cart`, { card_index: cardIdx, variant_id: variantId })
        }
        const cart = window.Spectrum && window.Spectrum.cart
        if (cart && typeof cart.add === 'function' && variantId) {
          ev.preventDefault()
          Promise.resolve(cart.add({ id: variantId, quantity: 1 })).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn(`[${FEATURE_SLUG}] add-to-cart failed`, err)
          })
        }
      })
    }

    // Card impression — fires once per card per page-load when ≥50% visible.
    // Gated by the per-brand impressions kill-switch via the `_impression`
    // event-name suffix (see snippet-library CLAUDE.md).
    if (track && typeof IntersectionObserver !== 'undefined') {
      const seen = new Set()
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue
            const card = e.target
            const idx = Number(card.dataset.spectrumCardIndex || '0')
            if (seen.has(idx)) continue
            seen.add(idx)
            track(`${FEATURE_SLUG}:product_impression`, {
              card_index: idx,
              title: cardTitle(card),
              url: cardUrl(card),
            })
          }
        },
        { threshold: 0.5 },
      )
      for (const card of cards) io.observe(card)
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiOgeszm9s = {
      safeTrack,
    }
  }

  const snippetApi = window.__spectrumAi?.snippet
  const containers = document.querySelectorAll(
    `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
  )
  for (const node of containers) {
    let track = noopTrack
    if (snippetApi && typeof snippetApi.bind === 'function') {
      const handles = snippetApi.bind(node, () => {
        // No variant-resolution work needed — the rail is server-rendered and
        // every prop the runtime might swap is captured by the wrapper
        // re-render on variant change.
      })
      if (handles?.track) track = safeTrack(handles.track)
    }
    initRail(node, track)
  }
})()
